import { combineReducers } from 'redux'
import { createAction, createAsyncAction, createReducer, isActionOf } from 'typesafe-actions'
import { ByUserQuery, StatisticsData, ByProjectData } from '../../../responses/statistics'
import * as O from 'fp-ts/Option'
import { Option } from 'fp-ts/Option'
import { RootEpic } from '../../root'
import { filter, map, mergeMap } from 'rxjs/operators'
import { apiRequests } from '../../../http/config'
import { toastFailedResponse } from '../../../components/controls/Toaster/Toaster'
import {
    HideConfiguration,
    OpenConfiguration,
    SortConfiguration
} from '../../../components/pages/statistics/StatisticsTable/configuration'
import { payloadReducer } from '../../../utils/redux-utils'
import { ID } from '../../../types/UtilityTypes'
import { SortDirection } from '../../../constants/direction'
import { User } from '../../../responses/users'
import {Direction} from '../../../responses/refs/refs'
import {RequestStatus} from '../../../responses/response'

export const byProjectActions = {
    data: createAsyncAction(
        '@STATISTICS_BY_PROJECT/DATA_REQUEST',
        '@STATISTICS_BY_PROJECT/DATA_SUCCESS',
        '@STATISTICS_BY_PROJECT/DATA_FAILURE',
    )<ByUserQuery, StatisticsData<ByProjectData>, void>(),
    setHiddenColumns: createAction('@STATISTICS_BY_PROJECT/SET_HIDDEN_COLUMNS')<HideConfiguration[]>(),
    toggleRow: createAction('@STATISTICS_BY_PROJECT/TOGGLE_ROW')<Record<ID, OpenConfiguration>>(),
    setManagers: createAction('@STATISTICS_BY_PROJECT/SET_MANAGERS')<User[]>(),
    setDirections: createAction('@STATISTICS_BY_PROJECT/SET_DIRECTIONS')<Direction[]>(),
    setUsers: createAction('@STATISTICS_BY_PROJECT/SET_USERS')<User[]>(),
    setSort: createAction('@STATISTICS_BY_PROJECT/SET_SORT')<SortConfiguration>(),
    clearFilters: createAction('@STATISTICS_BY_PROJECT/CLEAR_FILTERS')()
}

export interface ByProjectState {
    data: Option<StatisticsData<ByProjectData>>,
    managers: User[],
    directions: Direction[],
    users: User[],
    hide: HideConfiguration[],
    open: Record<ID, OpenConfiguration>,
    sort: SortConfiguration,
    state: RequestStatus,
}

export const byProjectReducer = combineReducers<ByProjectState>({
    data: createReducer<Option<StatisticsData<ByProjectData>>>(O.none)
        .handleAction(byProjectActions.data.success, (_, action) => O.of(action.payload)),
    managers: createReducer<User[]>([])
        .handleAction(byProjectActions.setManagers, payloadReducer)
        .handleAction(byProjectActions.clearFilters, () => []),
    directions: createReducer<Direction[]>([])
        .handleAction(byProjectActions.setDirections, payloadReducer)
        .handleAction(byProjectActions.clearFilters, () => []),
    users: createReducer<User[]>([])
        .handleAction(byProjectActions.setUsers, payloadReducer)
        .handleAction(byProjectActions.clearFilters, () => []),
    hide: createReducer<HideConfiguration[]>([])
        .handleAction(byProjectActions.setHiddenColumns, payloadReducer),
    open: createReducer<Record<ID, OpenConfiguration>>({})
        .handleAction(byProjectActions.data.success, () => ({}))
        .handleAction(byProjectActions.toggleRow, payloadReducer),
    sort: createReducer<SortConfiguration>({group: '', name: '', direction: SortDirection.ASC})
        .handleAction(byProjectActions.setSort, payloadReducer),
    state: createReducer<RequestStatus>(RequestStatus.INITIAL)
        .handleAction(byProjectActions.data.request, () => RequestStatus.FETCHING)
        .handleAction(byProjectActions.data.success, () => RequestStatus.SUCCESS)
        .handleAction(byProjectActions.data.failure, () => RequestStatus.FAILURE),
})

export const byClientEpic: RootEpic = action$ => action$.pipe(
    filter(isActionOf(byProjectActions.data.request)),
    mergeMap(action => apiRequests.statistics.byProject({query: action.payload})),
    map(({response}) => response.success ?
        byProjectActions.data.success(response) :
        byProjectActions.data.failure(toastFailedResponse(response, 'Не удалось запросить данные диаграммы'))
    ),
)
export const byProjectEpic: RootEpic = action$ => action$.pipe(
    filter(isActionOf(byProjectActions.data.request)),
    mergeMap(action => apiRequests.statistics.byProject({
        query: action.payload
    })),
    map(res => res.response.success?
        byProjectActions.data.success(res.response):
        byProjectActions.data.failure(toastFailedResponse(res.response, 'Загрузка статистики по проектам не удалась'))
    )
)